import { Box, Button, Grid2 as Grid, Typography } from "@mui/material"
import Stack from "@mui/material/Stack"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useTranslation } from "react-i18next"
import DialogForm from "../components/DialogForm"
import Layout from "../components/Layout"
import RevisioneForm from "../components/forms/RevisioneForm"
import { useQsFilters } from "../hooks/filters"
import { useRevisioneCreate, useRevisioniList, useRevisioneDelete, useRevisioneUpdate } from "../hooks/revisioni"
import { Revisione } from "../types"
import RevisioneCard from "../components/RevisioneCard"
import DialogConfirm from "../components/DialogConfirm"
import { useSnackbarMessage } from "../hooks/snackbar"
import { useIsMobile } from "../hooks/mobile"
import useUserCanWriteAndDelete from "../hooks/permissions"
import { useSupporto } from "../hooks/supporti"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "titolo",
  formato: params.get("formato") ?? "",
  elemento: params.get("elemento") ?? "",
})

type RevisioniProps = {
  supportoId: number
}

export default function Revisioni({ supportoId }: RevisioniProps) {
  const { t } = useTranslation()
  const { filters } = useQsFilters(initFilters)
  const userCanWriteAndDelete = useUserCanWriteAndDelete()
  const isMobile = useIsMobile()

  const { data } = useRevisioniList({
    page: String(filters.page),
    search: filters.search,
    ordering: filters.ordering,
    supporto: supportoId.toString(),
  })

  const { data: supporto, refetch: refetchSupporto } = useSupporto(supportoId)

  const { mutateAsync: create } = useRevisioneCreate()
  const { mutateAsync: update } = useRevisioneUpdate()
  const { mutateAsync: remove } = useRevisioneDelete()

  const [createModalState, createModalActions] = useModalTrigger()
  const [updateModalState, updateModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()

  return (
    <Layout>
      <Stack sx={{ minHeight: "100%" }}>
        <Box
          sx={{
            textAlign: "right",
            position: "sticky",
            top: 0,
            zIndex: 1,
            justifyContent: "flex-end",
            backgroundColor: "grey.100",
            borderBottom: 1,
            borderColor: "grey.300",
          }}
          px={3}
          py={1}
        >
          {userCanWriteAndDelete && !isMobile && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                createModalActions.open()
              }}
            >
              {t("revisione.cta_aggiungi")}
            </Button>
          )}
        </Box>

        <Box flexGrow={1} px={3} pb={3} pt={1}>
          <Grid container spacing={2}>
            {data?.results.map((revisione) => (
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 6,
                  lg: 6,
                  xl: 4,
                }}
                key={revisione.id}
              >
                <RevisioneCard
                  revisione={revisione}
                  onUpdate={() => {
                    updateModalActions.open(revisione)
                  }}
                  onDelete={() => {
                    deleteModalActions.open(revisione)
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>

      <DialogForm<Revisione>
        open={createModalState.isOpen}
        hasCancel
        handleClose={createModalActions.close}
        title={t("revisione.cta_aggiungi")}
        subtitle={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
            }}
          >
            <Typography variant="body2" component="div">
              {supporto?.segnatura}
              <br />
              <b>{t("supporto.formato")}:</b> {supporto?.formato_data?.nome || "N/A"}&nbsp;&nbsp;
              <b>{t("supporto.sist_colore")}:</b> {supporto?.sist_colore_data?.termine || "N/A"}&nbsp;&nbsp;
              <b>{t("supporto.cromatismo")}:</b> {supporto?.cromatismo_data?.termine || "N/A"}&nbsp;&nbsp;
              <b>{t("supporto.audio")}:</b> {supporto?.audio_data?.termine || "N/A"}&nbsp;&nbsp;
            </Typography>
          </Box>
        }
        FormComponent={RevisioneForm}
        save={(values: Revisione) => {
          const newValues = {
            ...values,
            supporto: supportoId,
          }
          return create(newValues as Revisione).then(() => {
            createModalActions.close()
            refetchSupporto()
          })
        }}
      />
      <DialogForm<Revisione>
        open={updateModalState.isOpen}
        hasCancel
        handleClose={updateModalActions.close}
        title={t("revisione.cta_modifica")}
        subtitle={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
            }}
          >
            <Typography variant="body2" component="div">
              {supporto?.segnatura}
              <br />
              <b>{t("supporto.formato")}:</b> {supporto?.formato_data?.nome || "N/A"}&nbsp;&nbsp;
              <b>{t("supporto.sist_colore")}:</b> {supporto?.sist_colore_data?.termine || "N/A"}&nbsp;&nbsp;
              <b>{t("supporto.cromatismo")}:</b> {supporto?.cromatismo_data?.termine || "N/A"}&nbsp;&nbsp;
              <b>{t("supporto.audio")}:</b> {supporto?.audio_data?.termine || "N/A"}&nbsp;&nbsp;
            </Typography>
          </Box>
        }
        FormComponent={RevisioneForm}
        item={updateModalState.value}
        save={(values: Revisione) => {
          return update(values).then(() => {
            updateModalActions.close()
            refetchSupporto()
            showMessage(t("revisione.esito_modifica_ok"))
          })
        }}
      />

      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("revisione.cta_elimina")}
        subtitle={
          <span>
            {t("revisione.cta_subtitle_elimina")} <b>"{deleteModalState.value?.data}"</b>&nbsp;?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              refetchSupporto()
              showMessage(t("revisione.esito_elimina_ok"))
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />
    </Layout>
  )
}
